import { useEffect, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import Info from '@mui/icons-material/Info'

// Each state maps to the list of IANA time zones that exist within it.
// Several states span multiple zones; until we have a finer-grained
// location-to-timezone signal, we surface every possibility so the user
// can resolve the ambiguity from the patient's specific address.
const STATE_TIMEZONES: Record<string, string[]> = {
  AL: ['America/Chicago'],
  AK: ['America/Juneau', 'America/Adak'],
  AR: ['America/Chicago'],
  // Most of AZ stays on MST year-round; the Navajo Nation observes DST
  // and uses Mountain Time (represented here by the America/Shiprock
  // tzdata link). The dedupe step in the renderer collapses these to a
  // single value during winter when both abbreviations are MST.
  AZ: ['America/Phoenix', 'America/Shiprock'],
  CA: ['America/Los_Angeles'],
  CO: ['America/Denver'],
  CT: ['America/New_York'],
  DE: ['America/New_York'],
  DC: ['America/New_York'],
  FL: ['America/New_York', 'America/Chicago'],
  GA: ['America/New_York'],
  HI: ['Pacific/Honolulu'],
  ID: ['America/Boise', 'America/Los_Angeles'],
  IL: ['America/Chicago'],
  IN: ['America/Indiana/Indianapolis', 'America/Indiana/Knox'],
  IA: ['America/Chicago'],
  KS: ['America/Chicago', 'America/Denver'],
  KY: ['America/New_York', 'America/Chicago'],
  LA: ['America/Chicago'],
  MA: ['America/New_York'],
  ME: ['America/New_York'],
  MD: ['America/New_York'],
  MI: ['America/Detroit', 'America/Menominee'],
  MN: ['America/Chicago'],
  MS: ['America/Chicago'],
  MO: ['America/Chicago'],
  MT: ['America/Denver'],
  NE: ['America/Chicago', 'America/Denver'],
  NV: ['America/Los_Angeles'],
  NH: ['America/New_York'],
  NJ: ['America/New_York'],
  NM: ['America/Denver'],
  NC: ['America/New_York'],
  ND: ['America/Chicago', 'America/Denver'],
  NY: ['America/New_York'],
  OH: ['America/New_York'],
  OK: ['America/Chicago'],
  OR: ['America/Los_Angeles', 'America/Boise'],
  PA: ['America/New_York'],
  RI: ['America/New_York'],
  SC: ['America/New_York'],
  SD: ['America/Chicago', 'America/Denver'],
  TN: ['America/Chicago', 'America/New_York'],
  TX: ['America/Chicago', 'America/Denver'],
  UT: ['America/Denver'],
  VT: ['America/New_York'],
  VA: ['America/New_York'],
  WA: ['America/Los_Angeles'],
  WV: ['America/New_York'],
  WI: ['America/Chicago'],
  WY: ['America/Denver'],
}

const patientTimezones = (patientState?: string | null): string[] | undefined => {
  if (!patientState) return
  return STATE_TIMEZONES[patientState]
}

export const PatientStateAndTimeInfo: React.FC<{
  patientState: string | null | undefined
}> = ({ patientState }) => {
  // Show the patient's state and, if there is a timezone difference
  // between the patient's state and the current user,
  // also show the current time in the patient's timezone
  const [date, setDate] = useState(() => new Date())
  const timezones = patientTimezones(patientState)
  const hasTimezones = !!timezones?.length

  useEffect(() => {
    if (!hasTimezones) return
    const clockUpdateInterval = setInterval(() => setDate(new Date()), 1000)
    return () => clearInterval(clockUpdateInterval)
  }, [hasTimezones])

  if (!timezones?.length) return <>{patientState}</>

  // For a state with a single time zone, render the existing format
  // ("MA (1:00 PM)"). For a state that spans multiple zones, format each
  // candidate time with its short zone name and dedupe so identical
  // formatted values collapse (e.g. when two zones happen to share an
  // offset and abbreviation right now).
  const isAmbiguous = timezones.length > 1
  const formattedTimes = timezones.map(tz =>
    isAmbiguous
      ? date.toLocaleTimeString('en-US', {
          timeZone: tz,
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        })
      : date.toLocaleTimeString('en-US', {
          timeZone: tz,
          timeStyle: 'short',
        }),
  )
  const uniqueTimes = Array.from(new Set(formattedTimes))

  if (uniqueTimes.length === 1) {
    return <>{`${patientState} (${uniqueTimes[0]})`}</>
  }

  return (
    <Tooltip
      title={`${patientState} spans multiple time zones; verify the patient's specific location.`}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        {`${patientState} (${uniqueTimes.join(' / ')})`}
        <Info sx={{ fontSize: '14px', color: '#878D96' }} />
      </Box>
    </Tooltip>
  )
}
